import { DiscountType } from '../models/discount/dto/discount.dto';
import { AllChargeableServicesPricesDiscounts, PriceDiscountData, PricingDto } from '../models/pricing/dto/pricing.dto';
import { Pricing } from '../models/pricing/pricing';
import { AddonServiceEnum, AllChargeableServices, BundleService, DocumentServiceEnum, ILocaleDetails, MembershipEnum } from '../services/localization/localization.service';

export class PricingUtils {
    private constructor() {}

    static getFallbackPrices(pricing: ILocaleDetails['pricing']): Partial<AllChargeableServicesPricesDiscounts> {
        const fallbackPrices: Partial<AllChargeableServicesPricesDiscounts> = {};
        const addPrice = (service: AllChargeableServices, configuredPrice: number | undefined, lookupKey?: string): void => {
            if (!Number.isFinite(configuredPrice) || configuredPrice === undefined || configuredPrice <= 0) return;
            fallbackPrices[service] = [...(fallbackPrices[service] ?? []), PricingUtils._priceData(configuredPrice, lookupKey)];
        };

        const lookupPricedServices: AllChargeableServices[] = [
            DocumentServiceEnum.LastWillAndTestament,
            DocumentServiceEnum.enduringPowerOfAttorney,
            DocumentServiceEnum.premiumLastWillAndTestament,
            BundleService.EstatePlanningBundle,
            AddonServiceEnum.EstatePlanningSession,
        ];
        const configuredServices = [
            ...Object.values(DocumentServiceEnum),
            ...Object.values(MembershipEnum),
            ...Object.values(AddonServiceEnum),
            ...Object.values(BundleService),
        ] as AllChargeableServices[];

        configuredServices.filter(service => !lookupPricedServices.includes(service)).forEach(service => addPrice(service, pricing[service]));

        addPrice(
            DocumentServiceEnum.LastWillAndTestament,
            pricing[DocumentServiceEnum.LastWillAndTestament],
            Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.LastWillAndTestament].willSingle
        );
        addPrice(DocumentServiceEnum.LastWillAndTestament, pricing.willCouple, Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.LastWillAndTestament].willCouple);

        addPrice(
            BundleService.EstatePlanningBundle,
            pricing[BundleService.EstatePlanningBundle],
            Pricing.PRODUCT_PRICE_KEYS[BundleService.EstatePlanningBundle].estatePlanningBundleSingle
        );
        addPrice(BundleService.EstatePlanningBundle, pricing.bundleCouple, Pricing.PRODUCT_PRICE_KEYS[BundleService.EstatePlanningBundle].estatePlanningBundleCouple);
        addPrice(
            BundleService.EstatePlanningBundle,
            pricing[BundleService.EstatePlanningBundle],
            Pricing.PRODUCT_PRICE_KEYS[BundleService.EstatePlanningBundle].estatePlanningBundleSingleWitnessing
        );
        addPrice(BundleService.EstatePlanningBundle, pricing.bundleCouple, Pricing.PRODUCT_PRICE_KEYS[BundleService.EstatePlanningBundle].estatePlanningBundleCoupleWitnessing);

        addPrice(
            AddonServiceEnum.EstatePlanningSession,
            pricing[AddonServiceEnum.EstatePlanningSession],
            Pricing.PRODUCT_PRICE_KEYS[AddonServiceEnum.EstatePlanningSession].estatePlanningSessionNewClient
        );
        addPrice(
            AddonServiceEnum.EstatePlanningSession,
            pricing[AddonServiceEnum.EstatePlanningSession],
            Pricing.PRODUCT_PRICE_KEYS[AddonServiceEnum.EstatePlanningSession].estatePlanningSessionReturningClient
        );

        addPrice(
            DocumentServiceEnum.enduringPowerOfAttorney,
            pricing[DocumentServiceEnum.enduringPowerOfAttorney],
            Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.enduringPowerOfAttorney].enduringPowerOfAttorneyDocument
        );
        addPrice(
            DocumentServiceEnum.enduringPowerOfAttorney,
            pricing.enduringPowerOfAttorneyWithRegistration,
            Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.enduringPowerOfAttorney].enduringPowerOfAttorneyDocumentWithRegistration
        );

        addPrice(
            DocumentServiceEnum.premiumLastWillAndTestament,
            pricing[DocumentServiceEnum.premiumLastWillAndTestament],
            Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.premiumLastWillAndTestament].premiumWillSingle
        );
        addPrice(
            DocumentServiceEnum.premiumLastWillAndTestament,
            pricing.premiumWillCouple,
            Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.premiumLastWillAndTestament].premiumWillCouple
        );

        return fallbackPrices;
    }

    static mergeWithFallbackPrices(pricingDto: PricingDto, configuredPricing: ILocaleDetails['pricing']): PricingDto {
        const services = PricingUtils.getFallbackPrices(configuredPricing);

        for (const [service, backendPrices] of Object.entries(pricingDto.services) as [AllChargeableServices, PriceDiscountData[] | undefined][]) {
            const validBackendPrices = backendPrices?.filter(PricingUtils._isValidPriceData) ?? [];
            if (!validBackendPrices.length) continue;

            // Older Premium responses supply a single price without a lookup key.
            // Its service discount also applies to the configured couple price.
            if (service === DocumentServiceEnum.premiumLastWillAndTestament && backendPrices?.length === 1 && !validBackendPrices[0].lookupKey) {
                validBackendPrices[0] = {
                    ...validBackendPrices[0],
                    lookupKey: Pricing.PRODUCT_PRICE_KEYS[DocumentServiceEnum.premiumLastWillAndTestament].premiumWillSingle,
                };
                const couplePrice = services[service]?.find(price => price.lookupKey === Pricing.PRODUCT_PRICE_KEYS[service].premiumWillCouple);
                const singlePrice = validBackendPrices[0];
                if (couplePrice && singlePrice.discount && !singlePrice.partner && singlePrice.discountedPrice !== undefined) {
                    const { discount, discountType } = singlePrice.discount;
                    const discountedPrice =
                        discountType === DiscountType.percentage
                            ? couplePrice.originalPrice * (1 - discount / 100)
                            : discountType === DiscountType.fixed
                            ? couplePrice.originalPrice - discount
                            : NaN;
                    if (Number.isFinite(discountedPrice) && discountedPrice > 0 && discountedPrice < couplePrice.originalPrice) {
                        validBackendPrices.push({ ...couplePrice, discount: singlePrice.discount, discountedPrice });
                    }
                }
            }

            const backendLookupKeys = new Set(validBackendPrices.map(price => price.lookupKey));
            const remainingFallbackPrices = (services[service] ?? []).filter(price => !backendLookupKeys.has(price.lookupKey));
            services[service] = [...validBackendPrices, ...remainingFallbackPrices];
        }

        return {
            ...pricingDto,
            services,
        };
    }

    private static _priceData(originalPrice: number, lookupKey?: string): PriceDiscountData {
        return {
            originalPrice,
            discount: undefined,
            discountedPrice: undefined,
            lookupKey,
        };
    }

    private static _isValidPriceData(price: PriceDiscountData): boolean {
        return (
            Number.isFinite(price.originalPrice) &&
            price.originalPrice > 0 &&
            (price.discountedPrice === undefined || (Number.isFinite(price.discountedPrice) && price.discountedPrice > 0))
        );
    }
}
